import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChatCircleDots, PaperPlaneRight } from "@phosphor-icons/react";

export const LiveChat = () => {
  const { user, login } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/chat");
      setMessages(res.data);
    } catch (e) {
      console.error("Failed to load chat", e);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
    // load is stable; polling interval intentionally set up once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!user) return login();
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      const res = await api.post("/chat", { text: t });
      setMessages((m) => [...m, res.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    }
  };

  return (
    <div className="bg-black/60 backdrop-blur-2xl border border-white/10 rounded-2xl flex flex-col h-[70vh] lg:h-full lg:max-h-[820px]">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <h3 className="font-head font-bold text-lg tracking-tight flex items-center gap-2">
          <ChatCircleDots size={20} weight="fill" className="text-[#B14AED]" /> Live Room
        </h3>
        <span className="flex items-center gap-1.5 font-mono text-xs text-[#A1A1AA]">
          <span className="w-2 h-2 rounded-full bg-[#E1FF00] animate-pulse" /> live
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
        {messages.map((m) => (
          <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3">
            <Avatar className="h-7 w-7 shrink-0 mt-0.5">
              <AvatarImage src={m.picture} />
              <AvatarFallback className="bg-[#0038FF] text-white text-[10px]">{m.name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <span className="font-semibold text-sm text-[#E1FF00]">{m.name}</span>
              <p className="text-sm text-[#E8E8EA] break-words leading-snug">{m.text}</p>
            </div>
          </motion.div>
        ))}
        {messages.length === 0 && (
          <div className="text-center text-[#52525B] text-sm pt-10">No messages yet. Start the conversation.</div>
        )}
      </div>

      <div className="p-3 border-t border-white/10">
        <div className="flex gap-2">
          <Input
            data-testid="live-chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={user ? "Say something to the room…" : "Sign in to chat"}
            className="bg-[#0F0F13] border-white/10 text-white rounded-none focus-visible:ring-2 focus-visible:ring-[#B14AED] focus-visible:ring-offset-0"
          />
          <Button
            data-testid="live-chat-submit"
            onClick={send}
            className="bg-[#B14AED] hover:bg-[#9d3ed4] text-white rounded-none shrink-0 px-4"
          >
            <PaperPlaneRight size={18} weight="fill" />
          </Button>
        </div>
      </div>
    </div>
  );
};
